import { useEffect } from 'react';

import lang from '../../lib/language';
import { MarketCountryCode } from '../../lib/models/fundamental/types';
import MarketHoursState from '../../lib/store-util/MarketHoursState';
import { useMarketState } from '../../lib/util/hooks/useMarketState';

import iconClose from '../../assets/img/close-line.svg';
import BaseModal from '../../shared_components/BaseModal/BaseModal';
import ClickableImage from '../../shared_components/ClickableImage/ClickableImage';
import useIsMobile from '../../shared_components/hooks/useIsMobile';
import MobileDrawer from '../../shared_components/MobileDrawer/MobileDrawer';

import { getMarketStateWithTimes } from './market-states-modal-helpers';

import styles from './MarketStatesModal.module.scss';

type Props = {
  countryCode?: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
};

const MarketStatesModal = (props: Props): JSX.Element => {
  const { countryCode = MarketCountryCode.UnitedStates, isOpen, setIsOpen } = props;
  const { isMobile } = useIsMobile();

  const { marketHours, nonWorkingDays } = MarketHoursState.get(countryCode);
  const { marketState, refreshMarketState } = useMarketState(countryCode);

  const marketStates = getMarketStateWithTimes(marketHours, marketState, nonWorkingDays);

  useEffect(() => {
    if (isOpen) {
      refreshMarketState();
    }
  }, [isOpen, refreshMarketState]);

  const renderBody = () => {
    return (
      <>
        <div className={styles.description}>
          {lang.commonTimesDisplayedLocalTime()}
        </div>
        <div className={styles.marketStateRowsWrapper}>
          {marketStates.map((state) => (
            <div key={state.key} className={styles.marketStateRow}>
              <div
                className={styles.verticalLine}
                style={{ backgroundColor: state.verticalLineColor }}
              />
              <div className={styles.marketStateContent}>
                <div className={styles.marketStateTitleAndIcon}>
                  <div className={styles.marketStateTitle} style={{ color: state.textColor }}>
                    {state.title}
                  </div>
                  {state.iconBackgroundColor && (
                    <div
                      className={styles.marketStateIconWrapper}
                      style={{ backgroundColor: state.iconBackgroundColor }}
                    >
                      <img src={state.icon} alt={state.iconAlt} className={styles.marketStateIcon} />
                    </div>
                  )}
                </div>
                <div className={styles.marketStateTimeText} style={{ color: state.textColor }}>
                  {state.timeText}
                </div>
                {state.description && (
                  <div className={styles.marketStateDescription}>{state.description}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  if (isMobile) {
    return (
      <MobileDrawer title={lang.commonMarketHours()} isOpen={isOpen} onClose={() => setIsOpen(false)} usePortal>
        {renderBody()}
      </MobileDrawer>
    );
  }
  return (
    <BaseModal isOpen={isOpen} toggle={() => setIsOpen(false)}>
      <ClickableImage
        src={iconClose}
        className={styles.closeButton}
        alt="Close"
        onClick={() => setIsOpen(false)}
            />
      <div className={styles.title}>
        {lang.commonMarketHours()}
      </div>
      {renderBody()}
    </BaseModal>
  );
};

export default MarketStatesModal;
